import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LayersClearOutlinedIcon from '@mui/icons-material/LayersClearOutlined';
import {
  MAX_TABLE_SIZE,
  MIN_TABLE_SIZE,
  TABLE_SIZE_PRESETS,
} from './lib/defaults';
import { FieldLabel, FP, pillSx } from './lib/ui';

const SHAPES = ['square', 'round', 'rectangle', 'booth', 'bar'];

const PanelTitle = ({ eyebrow, title }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: '0.09em',
        color: FP.primary,
      }}
    >
      {eyebrow}
    </Typography>
    <Typography sx={{ fontWeight: 800, fontSize: 16, color: FP.ink }}>
      {title}
    </Typography>
  </Box>
);

const FloorPlanInspector = ({
  sections = [],
  sectionUuid,
  selectedTable,
  selectedPlacement,
  selectedObject,
  onUpdateCatalog,
  onUpdatePlacement,
  onMoveSection,
  onRotate,
  onDuplicate,
  onDeleteTable,
  onUpdateObject,
  onDeleteObject,
  onRemoveFromFloor,
}) => {
  const [name, setName] = useState('');
  const [capacity, setCapacity] = useState(4);

  useEffect(() => {
    if (selectedTable?.tableDetails) {
      setName(
        selectedTable.tableDetails.displayName ||
          selectedTable.tableDetails.name ||
          ''
      );
      setCapacity(selectedTable.tableDetails.capacity || 4);
    }
  }, [selectedTable]);

  if (!selectedTable && !selectedObject) {
    return (
      <Box
        sx={{
          p: 3,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          gap: 1,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            border: `2px dashed ${FP.line}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: FP.faint,
          }}
        >
          <LayersClearOutlinedIcon fontSize="small" />
        </Box>
        <Typography sx={{ fontWeight: 800, color: FP.ink }}>Inspector</Typography>
        <Typography variant="body2" sx={{ color: FP.sub, maxWidth: 200 }}>
          Select a table or object on the floor to edit it.
        </Typography>
      </Box>
    );
  }

  if (selectedObject) {
    return (
      <Box sx={{ p: 2 }}>
        <PanelTitle eyebrow="OBJECT" title={selectedObject.label || 'Object'} />
        <Stack spacing={1.5}>
          <TextField
            size="small"
            label="Label"
            value={selectedObject.label || ''}
            onChange={(e) =>
              onUpdateObject(selectedObject.id, { label: e.target.value })
            }
          />
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              label="W"
              type="number"
              value={Math.round(selectedObject.width)}
              onChange={(e) =>
                onUpdateObject(selectedObject.id, {
                  width: Number(e.target.value) || 24,
                })
              }
            />
            <TextField
              size="small"
              label="H"
              type="number"
              value={Math.round(selectedObject.height)}
              onChange={(e) =>
                onUpdateObject(selectedObject.id, {
                  height: Number(e.target.value) || 24,
                })
              }
            />
          </Stack>
          <Button
            variant="outlined"
            startIcon={<RotateRightIcon />}
            sx={{ textTransform: 'none' }}
            onClick={() =>
              onUpdateObject(selectedObject.id, {
                rotation: ((selectedObject.rotation || 0) + 90) % 360,
              })
            }
          >
            Rotate 90°
          </Button>
          <Button
            color="error"
            variant="outlined"
            startIcon={<DeleteOutlineIcon />}
            sx={{ textTransform: 'none' }}
            onClick={() => onDeleteObject(selectedObject.id)}
          >
            Delete object
          </Button>
        </Stack>
      </Box>
    );
  }

  const uuid = selectedTable.tableDetails?.uuid;
  const placement = selectedPlacement;

  return (
    <Box sx={{ p: 2 }}>
      <PanelTitle eyebrow="TABLE" title={name || 'Table'} />
      <Stack spacing={1.5}>
        <TextField
          size="small"
          label="Name"
          value={name}
          onChange={(e) => {
            const next = e.target.value;
            setName(next);
            // Persist into the plan immediately so Save does not keep the
            // auto-allocated T{n} name if the user never blurs the field.
            const trimmed = next.trim();
            if (trimmed) {
              onUpdateCatalog(uuid, {
                name: trimmed,
                displayName: trimmed,
              });
            }
          }}
          onBlur={() => {
            if (name.trim()) {
              onUpdateCatalog(uuid, {
                name: name.trim(),
                displayName: name.trim(),
              });
            }
          }}
        />
        <TextField
          size="small"
          label="Seats"
          type="number"
          value={capacity}
          onChange={(e) => {
            const next = Number(e.target.value) || 1;
            setCapacity(next);
            onUpdateCatalog(uuid, { capacity: next });
          }}
          onBlur={() => onUpdateCatalog(uuid, { capacity })}
        />

        {placement ? (
          <>
            <FormControl size="small" fullWidth>
              <InputLabel>Shape</InputLabel>
              <Select
                label="Shape"
                value={placement.shape || 'square'}
                onChange={(e) =>
                  onUpdatePlacement(uuid, { shape: e.target.value })
                }
                sx={{ textTransform: 'capitalize' }}
              >
                {SHAPES.map((shape) => (
                  <MenuItem key={shape} value={shape} sx={{ textTransform: 'capitalize' }}>
                    {shape}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <FieldLabel>Size</FieldLabel>
              <Stack
                direction="row"
                spacing={0.75}
                sx={{ mt: 1, flexWrap: 'wrap' }}
                useFlexGap
              >
                {TABLE_SIZE_PRESETS.map((preset) => {
                  const base = 140;
                  const targetW = Math.round(base * preset.scale);
                  const active = Math.round(placement.width) === targetW;
                  return (
                    <Button
                      key={preset.id}
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        onUpdatePlacement(uuid, {
                          width: targetW,
                          height: Math.round(base * preset.scale),
                        })
                      }
                      sx={pillSx(active)}
                    >
                      {preset.label}
                    </Button>
                  );
                })}
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
                <TextField
                  size="small"
                  label="W"
                  type="number"
                  inputProps={{ min: MIN_TABLE_SIZE, max: MAX_TABLE_SIZE }}
                  value={Math.round(placement.width)}
                  onChange={(e) =>
                    onUpdatePlacement(uuid, {
                      width: Number(e.target.value) || MIN_TABLE_SIZE,
                    })
                  }
                />
                <TextField
                  size="small"
                  label="H"
                  type="number"
                  inputProps={{ min: MIN_TABLE_SIZE, max: MAX_TABLE_SIZE }}
                  value={Math.round(placement.height)}
                  onChange={(e) =>
                    onUpdatePlacement(uuid, {
                      height: Number(e.target.value) || MIN_TABLE_SIZE,
                    })
                  }
                />
              </Stack>
            </Box>
          </>
        ) : null}

        <FormControl size="small" fullWidth>
          <InputLabel>Section</InputLabel>
          <Select
            label="Section"
            value={sectionUuid || ''}
            onChange={(e) => onMoveSection(uuid, e.target.value)}
          >
            {sections.map((section) => {
              const suuid = section?.sectionDetails?.uuid;
              const label =
                section?.sectionDetails?.displayName ||
                section?.sectionDetails?.name ||
                'Section';
              return (
                <MenuItem key={suuid} value={suuid}>
                  {label}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <Divider sx={{ my: 0.5 }} />

        <Stack direction="row" spacing={1}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<RotateRightIcon />}
            onClick={onRotate}
            disabled={!placement}
            sx={{ textTransform: 'none' }}
          >
            Rotate
          </Button>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={() => onDuplicate(uuid)}
            sx={{ textTransform: 'none' }}
          >
            Duplicate
          </Button>
        </Stack>
        {placement ? (
          <Button
            variant="outlined"
            startIcon={<LayersClearOutlinedIcon />}
            onClick={() => onRemoveFromFloor(uuid)}
            sx={{ textTransform: 'none', color: FP.sub, borderColor: FP.line }}
          >
            Remove from floor
          </Button>
        ) : null}
        <Button
          color="error"
          variant="outlined"
          startIcon={<DeleteOutlineIcon />}
          sx={{ textTransform: 'none' }}
          onClick={async () => {
            const err = await onDeleteTable(uuid);
            if (err) {
              window.alert(err);
            }
          }}
        >
          Delete table
        </Button>
      </Stack>
    </Box>
  );
};

export default FloorPlanInspector;
